/** Column definitions for the API-credentials grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget (the page also sets `disableColumnFilter`). Sortable
 * column FIELDS equal the backend ordering whitelist keys (id, tenant, name,
 * scope, status, type, created_ts, expires, last_used) so the sort model
 * round-trips as the DRF `ordering` param — everything else is
 * `sortable: false` (the dead-sort-header bug class). Cell popovers carry
 * copy actions and quick filters appending an equality chip to the search.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';

import type { ApiCredentialListItem } from '@/api/apiCredentials';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TsValue } from '@/features/bgtasks/taskFormat';

import { LegacyChip, ScopeChip, StatusChip, SystemChip } from './apiCredentialFormat';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

function copyAction(label: string, value: string): CellAction {
  return {
    key: 'copy',
    label,
    icon: <ContentCopyIcon fontSize="small" />,
    onClick: () => void navigator.clipboard.writeText(value),
  };
}

function filterAction(
  label: string,
  ns: string,
  value: string,
  onQuickFilter: BuildColumnsOptions['onQuickFilter'],
): CellAction {
  return {
    key: `filter-${ns}`,
    label,
    icon: <FilterAltIcon fontSize="small" />,
    onClick: () => onQuickFilter(ns, value),
  };
}

const dash = <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
  /** Append an exact `ns=value` chip to the search. */
  onQuickFilter: (namespace: string, value: string) => void;
  /** Route to a credential's detail drawer (keeps the grid's query string). */
  detailPath: (id: number) => string;
}

export function buildColumns(opts: BuildColumnsOptions): GridColDef<ApiCredentialListItem>[] {
  const { showTenant, onQuickFilter, detailPath } = opts;

  const cols: GridColDef<ApiCredentialListItem>[] = [];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 150,
      filterable: false,
      valueGetter: (_v, row) => row.tenant?.code ?? 'system',
      renderCell: (p) =>
        p.row.tenant ? (
          <WithTenantMark environment={p.row.tenant.environment}>
            <CellActionsPopover
              auto
              title={p.row.tenant.name}
              actions={[
                {
                  key: 'open',
                  label: 'Open tour operator',
                  icon: <OpenInNewIcon fontSize="small" />,
                  href: `/tour-operators/${p.row.tenant.id}`,
                },
                filterAction('Filter by this tenant', 'tenant', String(p.row.tenant.id), onQuickFilter),
              ]}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              <Box component="span" sx={mono}>{p.row.tenant.code}</Box>
            </CellActionsPopover>
          </WithTenantMark>
        ) : (
          <CellActionsPopover
            auto
            title="Platform credential (no tenant)"
            actions={[filterAction('Filter to system credentials', 'tenant', 'system', onQuickFilter)]}
          >
            <SystemChip />
          </CellActionsPopover>
        ),
    });
  }

  cols.push(
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={p.row.name}
          actions={[copyAction('Copy name', p.row.name)]}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          <Box component="span">{p.row.name}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'scope',
      headerName: 'Scope',
      width: 130,
      filterable: false,
      valueGetter: (_v, row) => row.scope,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[filterAction('Filter by this scope', 'scope', p.row.scope, onQuickFilter)]}
        >
          <ScopeChip scope={p.row.scope} />
        </CellActionsPopover>
      ),
    },
    {
      // partner_dev keys only. Not in the ordering whitelist.
      field: 'partner',
      headerName: 'Partner',
      width: 130,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.partner?.code ?? '',
      renderCell: (p) =>
        p.row.partner ? (
          <CellActionsPopover
            auto
            title={p.row.partner.name}
            actions={[
              {
                key: 'open',
                label: 'Open partner',
                icon: <OpenInNewIcon fontSize="small" />,
                href: `/partners/${p.row.partner.id}`,
              },
              // All digits: the partner namespace's iexact also matches the id.
              filterAction('Filter by this partner', 'partner', String(p.row.partner.id), onQuickFilter),
            ]}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <Box component="span" sx={mono}>{p.row.partner.code}</Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      filterable: false,
      valueGetter: (_v, row) => row.status,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[filterAction('Filter by this status', 'status', p.row.status, onQuickFilter)]}
        >
          <StatusChip status={p.row.status} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 110,
      filterable: false,
      valueGetter: (_v, row) => row.credential_type,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[filterAction('Filter by this type', 'type', p.row.credential_type, onQuickFilter)]}
        >
          <Box component="span" sx={mono}>{p.row.credential_type}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'key',
      headerName: 'Key',
      width: 200,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.key_fingerprint || row.key_prefix,
      renderCell: (p) =>
        p.row.key_prefix || p.row.key_fingerprint ? (
          <CellActionsPopover
            auto
            title={p.row.key_fingerprint}
            actions={[
              ...(p.row.key_fingerprint
                ? [
                    copyAction('Copy fingerprint', p.row.key_fingerprint),
                    filterAction(
                      'Filter by this fingerprint',
                      'fingerprint',
                      p.row.key_fingerprint,
                      onQuickFilter,
                    ),
                  ]
                : []),
            ]}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <Box component="span" sx={mono}>
              {p.row.key_prefix ? `${p.row.key_prefix}…` : p.row.key_fingerprint}
            </Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'legacy',
      headerName: 'Legacy',
      width: 90,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => (row.has_legacy_key ? 'yes' : ''),
      renderCell: (p) =>
        p.row.has_legacy_key ? (
          <CellActionsPopover
            auto
            title="Stores a plaintext legacy key (revealable, permission-gated)"
            actions={[filterAction('Filter to legacy keys', 'legacy', 'yes', onQuickFilter)]}
          >
            <LegacyChip />
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'created_ts',
      headerName: 'Created',
      width: 165,
      filterable: false,
      renderCell: (p) => (p.row.created_ts ? <TsValue value={p.row.created_ts} /> : dash),
    },
    {
      field: 'expires',
      headerName: 'Expires',
      width: 165,
      filterable: false,
      renderCell: (p) => {
        if (!p.row.expires) return dash;
        const past = new Date(p.row.expires).getTime() < Date.now();
        return (
          <Box component="span" sx={past ? { color: 'error.main' } : undefined}>
            <TsValue value={p.row.expires} />
          </Box>
        );
      },
    },
    {
      field: 'last_used',
      headerName: 'Last used',
      width: 165,
      filterable: false,
      renderCell: (p) => (p.row.last_used ? <TsValue value={p.row.last_used} /> : dash),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={String(p.row.id)}
          actions={[
            copyAction('Copy credential id', String(p.row.id)),
            {
              key: 'open',
              label: 'Open credential',
              icon: <OpenInNewIcon fontSize="small" />,
              href: detailPath(p.row.id),
            },
          ]}
        >
          <Box component="span" sx={mono}>{p.row.id}</Box>
        </CellActionsPopover>
      ),
    },
  );

  return cols;
}
